import { RECURRING_RECEIVABLES_URL } from 'constants/config';
import { PERMANENT_COOKIE_NAME } from 'constants/cookies';
import type { Decamelized } from 'humps';
import type { Payment } from 'models/payment';
import { cookies } from 'utils/cookies/cookies';
import { fetchWithAuthentication } from 'utils/fetchJson/fetchJson';
import type {
  ChangeCardActivePlansRequestBody,
  CheckActivePlansResponse,
  CreateRecurringParams,
  FetchRecurringReceivablesResponse,
  GetBalanceResponse,
  GetInstallmentBalanceParams,
  GetRemainingBalanceParams,
  PaginationParams,
  PayInstallmentBalanceParams,
  PayRemainingBalanceParams,
  UpdatePaymentMethodParams,
} from './types';

export const recurringReceivables = {
  fetch: ({ perPage = 5, page = 1 }: PaginationParams = {}) => {
    const options = { method: 'GET' };

    return fetchWithAuthentication<FetchRecurringReceivablesResponse>(
      `${RECURRING_RECEIVABLES_URL}/details?per_page=${perPage}&page=${page}`,
      options,
    );
  },

  cancel: (id: string, locale: string) => {
    const url = `${RECURRING_RECEIVABLES_URL}/${id}/cancellation?locale=${locale}`;
    const options = { method: 'POST', body: JSON.stringify({}) };

    return fetchWithAuthentication(url, options);
  },

  create: ({
    fingerprint = '',
    isFromGateway,
    locale = 'EN',
    numberOfInstallments,
    orderId,
    orderToken,
    nonAutomated,
    configurationId,
    ...params
  }: CreateRecurringParams) => {
    const url = `${RECURRING_RECEIVABLES_URL}?locale=${locale}&fingerprint=${fingerprint}`;
    const body = JSON.stringify({
      number_of_installments: numberOfInstallments,
      order_id: orderId,
      order_token: orderToken,
      non_automated: nonAutomated,
      configuration_id: configurationId,
      ...(!nonAutomated && {
        api_confirmation_url: params.apiConfirmationURL,
        session_id: params.sessionId,
      }),
      ...(isFromGateway && { is_from_gateway: isFromGateway }),
    });

    const options = { method: 'POST', body };
    return fetchWithAuthentication<{ id: string }>(url, options);
  },

  checkCardActivePlans: async (token: string) => {
    const url = `${RECURRING_RECEIVABLES_URL}/active_plans_search`;
    const body = JSON.stringify({
      token,
    });

    const options = { method: 'POST', body };
    return await fetchWithAuthentication<CheckActivePlansResponse>(
      url,
      options,
    );
  },

  changeCardActivePlans: ({
    oldToken,
    newToken,
    confirmUrl,
    confirmToken,
    sessionId,
  }: ChangeCardActivePlansRequestBody) => {
    const url = `${RECURRING_RECEIVABLES_URL}/active_plans`;
    const body = JSON.stringify({
      old_token: oldToken,
      ...(newToken && { new_token: newToken }),
      ...(confirmUrl && { confirm_url: confirmUrl }),
      ...(confirmToken && { confirm_token: confirmToken }),
      ...(sessionId && { session_id: sessionId }),
    });

    const options = { method: 'POST', body };
    return fetchWithAuthentication(url, options);
  },

  getRemainingBalance: ({ id, locale }: GetRemainingBalanceParams) => {
    const fingerprint = cookies.read(PERMANENT_COOKIE_NAME);

    const url = `${RECURRING_RECEIVABLES_URL}/${id}/remaining_balance?locale=${locale}&fingerprint=${fingerprint}`;

    return fetchWithAuthentication<GetBalanceResponse>(url, {
      method: 'GET',
    });
  },

  payRemainingBalance: ({
    id,
    frozenOfferId,
    chargeIntent,
    manualPayment = false,
    withNewCard = false,
  }: PayRemainingBalanceParams): Promise<Payment> => {
    const url = `${RECURRING_RECEIVABLES_URL}/${id}/pay_balance`;

    let params: Decamelized<Omit<PayRemainingBalanceParams, 'id'>> = {
      frozen_offer_id: frozenOfferId,
      manual_payment: manualPayment,
      with_new_card: withNewCard,
    };

    if (chargeIntent) {
      const { apiConfirmationUrl, sessionId } = chargeIntent;
      params = {
        ...params,
        charge_intent: {
          api_confirmation_url: apiConfirmationUrl,
          session_id: sessionId,
        },
      };
    }

    const options = {
      method: 'POST',
      body: JSON.stringify(params),
    };
    return fetchWithAuthentication(url, options);
  },
  getInstallmentBalance: ({
    id,
    installmentId,
    locale,
  }: GetInstallmentBalanceParams) => {
    const fingerprint = cookies.read(PERMANENT_COOKIE_NAME);

    const url = `${RECURRING_RECEIVABLES_URL}/${id}/installments/${installmentId}/balance?locale=${locale}&fingerprint=${fingerprint}`;

    return fetchWithAuthentication<GetBalanceResponse>(url, {
      method: 'GET',
    });
  },

  payInstallmentBalance: ({
    id,
    installmentId,
    frozenOfferId,
    chargeIntent,
    manualPayment = false,
    withNewCard = false,
  }: PayInstallmentBalanceParams): Promise<Payment> => {
    const url = `${RECURRING_RECEIVABLES_URL}/${id}/installments/${installmentId}/pay`;

    let params: Decamelized<
      Omit<PayInstallmentBalanceParams, 'id' | 'installmentId'>
    > = {
      frozen_offer_id: frozenOfferId,
      manual_payment: manualPayment,
      with_new_card: withNewCard,
    };

    if (chargeIntent) {
      const { apiConfirmationUrl, sessionId } = chargeIntent;
      params = {
        ...params,
        charge_intent: {
          api_confirmation_url: apiConfirmationUrl,
          session_id: sessionId,
        },
      };
    }

    const options = {
      method: 'POST',
      body: JSON.stringify(params),
    };

    return fetchWithAuthentication(url, options);
  },

  updatePaymentMethod: ({
    id,
    sessionId,
    confirmUrl,
    withNewCard,
  }: UpdatePaymentMethodParams) => {
    const url = `${RECURRING_RECEIVABLES_URL}/${id}`;
    const body = JSON.stringify({
      session_id: sessionId,
      confirm_url: confirmUrl,
      with_new_card: withNewCard,
    });

    const options = { method: 'PATCH', body };
    return fetchWithAuthentication(url, options);
  },
};
